<template>
  <div id="header">
    <div class="header-logo-outer">
      <router-link
        :to="tokenExists ? this.$router.currentRoute : '/view/dashboard'"
        class="header-logo"
      >
        <img
          src="/img/siemens-energy-logo.png"
          alt
          srcset
          style="width: inherit"
        />
      </router-link>
    </div>
    <div
      class="header-inner header-current-tool"
      data-testid="header-current-tool"
    >
      DIJ - {{ toolName ? toolName : 'Digital Inspection Jacket' }}
    </div>

    <router-link
      to="/view/admin"
      v-if="isAdminVisible && !isDashboard && !this.$route.query.token"
    >
      <div class="header-inner headerTool">{{ $t('admin') }}</div>
    </router-link>

    <router-link
      to="/view/editor"
      v-if="isEditorVisible && !isDashboard && !this.$route.query.token"
    >
      <div class="header-inner headerTool">{{ $t('editor') }}</div>
    </router-link>

    <router-link
      to="/view/publisher"
      v-if="isPublisherVisible && !isDashboard && !this.$route.query.token"
    >
      <div class="header-inner headerTool">{{ $t('publisher') }}</div>
    </router-link>

    <router-link
      to="/view/inspector"
      v-if="
        toolName !== 'Inspector' &&
        !tokenExists &&
        !isDashboard &&
        userAllowedTools.some((tool) => tool === 'inspector') &&
        !this.$route.query.token
      "
    >
      <div class="header-inner headerTool">{{ $t('inspector') }}</div>
    </router-link>

    <router-link
      to="/view/report"
      v-if="isReportVisible && !isDashboard && !this.$route.query.token"
    >
      <div v-if="!tokenExists && !isDashboard" class="header-inner headerTool">
        {{ $t('report') }}
      </div>
    </router-link>

    <submit-for-approval v-if="isToolEditor" />
    <HeaderMenu v-if="!tokenExists" />
  </div>
</template>

<script>
import {
  WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION,
  WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION,
} from '../../store/websocket/actions/actionTypes';
import SubmitForApproval from '../ToolsTemplates/TemplateApproval/SubmitForApproval';
import HeaderMenu from './HeaderMenu';

export default {
  name: 'main-header',
  components: {
    HeaderMenu,
    'submit-for-approval': SubmitForApproval,
  },
  data() {
    return {
      tokenExists: false,
      navigation: 'home',
      isToolEditor: false,
      isDashboard: false,
      token: '',
      reportGenerationToast: null,
      reportProgressPercent: 0,
      reportProgressStatus: '',
      reportToastTimeoutId: null,
      reportToastDurationMs: 20000,
    };
  },
  computed: {
    user() {
      return this.$store.state.currentUser;
    },
    userRoles() {
      return this.$store.state.userRoles;
    },
    userAllowedTools() {
      return this.$store.state.currentUserAllowedTools;
    },
    route() {
      return this.$route.path;
    },
    toolName() {
      const path = this.route.split('/')[2];
      let tool = path ? this.$t(`${path}`) : undefined;
      if (this.$route.path.includes('dashboard') || this.$route.path === '/') {
        tool = 'Dashboard';
      }
      return tool;
    },
    isMobile() {
      return (
        typeof window.orientation !== 'undefined' ||
        navigator.userAgent.indexOf('IEMobile') !== -1
      );
    },
    isEditorVisible() {
      return (
        this.userAllowedTools.some((tool) => tool === 'editor') &&
        !this.isMobile &&
        this.toolName !== 'Editor' &&
        !this.tokenExists
      );
    },
    isPublisherVisible() {
      return (
        this.userAllowedTools.some((tool) => tool === 'publisher') &&
        !this.isMobile &&
        this.toolName !== 'Publisher' &&
        !this.tokenExists
      );
    },
    isReportVisible() {
      return (
        this.userAllowedTools.some((tool) => tool === 'report') &&
        !this.isMobile &&
        this.toolName !== 'Report' &&
        !this.tokenExists
      );
    },
    isAdminVisible() {
      return (
        this.userAllowedTools.some((tool) => tool === 'admin') &&
        !this.isMobile &&
        this.toolName !== 'Admin' &&
        !this.tokenExists
      );
    },
  },
  watch: {
    '$route.path': {
      handler(path) {
        this.isDashboard = path.includes('dashboard') || path === '/';
      },
    },
    '$route.query': {
      handler(query) {
        if (query.token) {
          this.token = query.token;
          if (this.token) {
            this.tokenExists = true;
            if (this.$route.path.includes('editor')) {
              this.isToolEditor = true;
            }
          }
        }
      },
    },
  },
  async created() {
    const wsMappings = {
      reportResponseReady: this.onReportResponseReady,
      reportResponseStatus: this.onReportResponseStatus,
      downloadReportResponse: this.onDownloadReportResponse,
    };
    await Promise.all(
      Object.keys(wsMappings).map((key) =>
        this.$store.dispatch(WEBSOCKET_EVENT_SUBSCRIBE_TO_ACTION, {
          action: key,
          callback: wsMappings[key],
        })
      )
    );
  },
  beforeDestroy() {
    const wsMappings = {
      reportResponseReady: this.onReportResponseReady,
      reportResponseStatus: this.onReportResponseStatus,
      downloadReportResponse: this.onDownloadReportResponse,
    };
    Promise.all(
      Object.keys(wsMappings).map((key) =>
        this.$store.dispatch(WEBSOCKET_EVENT_UNSUBSCRIBE_FROM_ACTION, {
          action: key,
          callback: wsMappings[key],
        })
      )
    ).finally(() => {
      this.closeReportToast();
    });
  },
  mounted() {
    if (this.$route.path.includes('dashboard')) {
      this.isDashboard = true;
    }
    this.token = this.$route.query.token;
    if (this.token) {
      this.tokenExists = true;
      if (this.$route.path.includes('editor')) {
        this.isToolEditor = true;
      }
    }
  },
  methods: {
    openReportToast(initialMessage = 'Preparing report…') {
      const message = initialMessage || this.formatProgressMessage();

      if (!this.reportGenerationToast) {
        this.reportGenerationToast = this.$buefy.toast.open({
          duration: 0,
          message,
          type: 'is-warning',
          position: 'is-bottom',
          queue: false,
        });
      } else {
        this.reportGenerationToast.message = message;
      }

      this.resetReportToastTimeout();
    },
    updateReportToast(msg) {
      const message = msg || this.formatProgressMessage();

      if (!this.reportGenerationToast) {
        this.openReportToast(message);
        return;
      }

      this.reportGenerationToast.message = message;
      this.resetReportToastTimeout();
    },
    resetReportToastTimeout() {
      if (this.reportToastTimeoutId) {
        clearTimeout(this.reportToastTimeoutId);
        this.reportToastTimeoutId = null;
      }

      this.reportToastTimeoutId = setTimeout(() => {
        this.closeReportToast();
      }, this.reportToastDurationMs);
    },
    closeReportToast(delayMs = 0) {
      if (this.reportToastTimeoutId) {
        clearTimeout(this.reportToastTimeoutId);
        this.reportToastTimeoutId = null;
      }

      if (!this.reportGenerationToast) return;

      const closer = () => {
        if (this.reportGenerationToast) {
          this.reportGenerationToast.close();
          this.reportGenerationToast = null;
        }
      };

      if (delayMs > 0) setTimeout(closer, delayMs);
      else closer();
    },
    formatProgressMessage() {
      const pct = Number.isFinite(this.reportProgressPercent)
        ? Math.max(0, Math.min(100, Math.round(this.reportProgressPercent)))
        : null;
      const status =
        typeof this.reportProgressStatus === 'string'
          ? this.reportProgressStatus
          : '';
      const pctText = pct !== null ? ` ${pct}%` : '';
      const statusText = status ? ` — ${status}` : '';
      return `Generating report…${pctText}${statusText}`;
    },

    // ----- Existing handlers -----
    onDownloadReportResponse(resp) {
      if (resp.code === 200) {
        this.$buefy.toast.open({
          duration: 7000,
          message: 'Report generation request sent.',
          type: 'is-success',
          position: 'is-top',
        });
      } else {
        this.$buefy.toast.open({
          duration: 3000,
          message: 'Something went wrong when trying to generate report',
          type: 'is-danger',
          position: 'is-top',
        });
      }
      this.disabledDownload = false;
    },
    onReportResponseStatus(resp) {
      if (!resp || !resp.message) return;

      const msg = resp.message;
      const percent =
        Number(msg.progress) ??
        Number(msg.percentage) ??
        Number(msg.percent) ??
        (typeof msg.status === 'number' ? Number(msg.status) : NaN);

      const statusText =
        msg.label ||
        msg.phase ||
        (typeof msg.status === 'string' ? msg.status : '') ||
        '';

      if (!Number.isNaN(percent)) this.reportProgressPercent = percent;
      this.reportProgressStatus = statusText;

      this.openReportToast();
      this.updateReportToast(this.formatProgressMessage());

      if (
        Number.isFinite(this.reportProgressPercent) &&
        this.reportProgressPercent >= 100
      ) {
        this.closeReportToast(1500);
      }
    },

    triggerDownload(downloadLink) {
      const anchor = document.createElement('a');
      anchor.style.display = 'none';
      anchor.href = downloadLink;
      anchor.setAttribute('download', '');
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    },

    async onReportResponseReady(resp) {
      this.closeReportToast(0);

      const successToast = {
        duration: 6000,
        message: 'Your report has been successfully generated!',
        type: 'is-success',
        position: 'is-bottom',
      };

      const errorToast = {
        duration: 6000,
        message: 'Something went wrong when trying to generate the report',
        type: 'is-danger',
        position: 'is-bottom',
      };

      if (resp.code === 200) {
        this.$buefy.toast.open(successToast);

        // setTimeout(async () => {
        //   try {
        //     this.readyToDownload = true;
        //     this.docFileUrl = resp.path;
        //     this.triggerDownload(resp.path);
        //     if (this.job && this.job._id) {
        //       this.$store.dispatch('getJob', this.job._id);
        //     }
        //   } catch (error) {
        //     console.error('Error during post-download actions:', error);
        //     this.$buefy.toast.open(errorToast);
        //   }
        // }, 1500);
      } else {
        this.$buefy.toast.open(errorToast);
      }
    },
  },
};
</script>

<style scoped lang="scss">
#header {
  display: flex;
  position: static !important;
}
.header-logo-outer {
  width: 100px;
  height: 100%;
  margin: 8px 10px 10px 40px;
}
.header-logo {
  width: 100%;
  img {
    width: 90px !important;
    margin-top: 2px;
  }
}
.header-current-tool {
  margin-left: 30px;
}
#header .header-inner {
  position: relative;
  padding: 16px 0;
  vertical-align: top;
  display: inline-flex;
}
#header .header-inner-logo a {
  position: relative;
  height: 100px;
  margin: 0;
  top: 0;
  text-decoration: none;
  color: white;
  overflow-y: hidden;
}
.headerTool {
  padding: 13px 32px !important;
  border: 3px solid #afafaf;
  height: 50px;
}
.headerTool:hover {
  color: white !important;
  border-bottom: 3px solid white;
}
.offlineToggle {
  position: absolute;
  top: 13px;
  right: 180px;
}
.offlineToggle paper-toggle-button {
  --paper-toggle-button-unchecked-bar-color: #4a86e8;
  --paper-toggle-button-unchecked-button-color: #4a86e8;
  --paper-toggle-button-checked-bar-color: gray;
  --paper-toggle-button-checked-button-color: white;
}
.offlineToggle .offlineLabel {
  color: white;
  font-family: 'Siemens Sans';
}
</style>

<!-- Unscoped: center Buefy bottom toasts (applies to this and other bottom toasts) -->
<style lang="scss">
.notices.is-bottom {
  left: 50% !important;
  right: auto !important;
  transform: translateX(-50%);
  bottom: 0.75rem;
}
.notices.is-bottom .toast {
  margin-left: auto;
  margin-right: auto;
}
</style>
